import React, { useState, useEffect, useCallback, useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog, ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import {
  SyutGrid,
  type SyutGridColumn,
  SyutSelect,
  type ActionMenuItem,
} from 'components/ui';
import type { SelectOption } from 'components/ui/SyutSelect';
import { useApprovals } from 'hooks/api/useApprovals';
import type { Approval, ApprovalSearchRequest } from 'api/types/approval.types';
import { isPostArrivalItem, requestedBasisLabel } from 'api/types/approval.types';
import ApprovalActionModal from 'components/modals/modules/approval-module/ApprovalActionModal';
import { isSeniorActor, MODULE_IDS } from 'config/rolePermissions';
import { requestConfigGroup } from 'hooks/api/useConfigSettings';
import RateWorksheetModal from 'components/modals/modules/approval-module/RateWorksheetModal';
import ContributedApprovalsPanel from './ContributedApprovalsPanel';
import BookingViewModal from 'components/modals/modules/booking-module/BookingViewModal';
import { bookingService } from 'api/services/bookingService';
import type { Booking } from 'api/types/booking.types';
import { propertyService } from 'api/services/propertyService';
import { approvalService } from 'api/services/approvalService';
import notificationService from 'services/notificationService';
import { formatCurrency } from 'components/modals/modules/booking-module/booking.utils';

// ============================================================================
// CONSTANTS
// ============================================================================

const STATUS_OPTIONS: SelectOption[] = [
  { value: 'APPROVAL_PENDING', label: 'Pending' },
  { value: 'APPROVED',         label: 'Approved' },
  { value: 'REJECTED',         label: 'Rejected' },
  { value: 'ESCALATED',        label: 'Escalated' },
];

const TYPE_OPTIONS: SelectOption[] = [
  { value: 'DISCOUNT',           label: 'Discount' },
  { value: 'COMPLIMENTARY',      label: 'Complimentary' },
  { value: 'RATE_BELOW_MINIMUM', label: 'Rate Below Minimum' },
  { value: 'ROOM_UPGRADE',       label: 'Room Upgrade' },
  { value: 'REFUND',             label: 'Refund' },
  { value: 'ADVANCE_OVERRIDE',   label: 'Advance Override' },
  { value: 'OVERDUE_ADVANCE',    label: 'Overdue Advance' },
  { value: 'ARRIVAL_CHANGE',     label: 'Arrival Change' },
  { value: 'FO_CHANGE_REQUEST',  label: 'FO Request' },
  { value: 'NO_SHOW_PRICING',    label: 'No-Show Pricing' },
];

const STATUS_BADGE: Record<string, { bg: string; fg: string; label: string }> = {
  APPROVAL_PENDING: { bg: '#fef3c7', fg: '#92400e', label: 'Pending' },
  APPROVED:         { bg: '#dcfce7', fg: '#166534', label: 'Approved' },
  REJECTED:         { bg: '#fee2e2', fg: '#991b1b', label: 'Rejected' },
  ESCALATED:        { bg: '#dbeafe', fg: '#1e40af', label: 'Escalated' },
};

// Type chip colours — left border on rows + chip background.
const TYPE_STYLE: Record<string, { bg: string; fg: string; border: string; label: string }> = {
  DISCOUNT:           { bg: '#dbeafe', fg: '#1e40af', border: '#3b82f6', label: 'Discount' },
  COMPLIMENTARY:      { bg: '#ede9fe', fg: '#5b21b6', border: '#8b5cf6', label: 'Complimentary' },
  RATE_BELOW_MINIMUM: { bg: '#fee2e2', fg: '#991b1b', border: '#dc2626', label: 'Rate < Min' },
  ROOM_UPGRADE:       { bg: '#d1fae5', fg: '#065f46', border: '#10b981', label: 'Room Upgrade' },
  REFUND:             { bg: '#ffedd5', fg: '#9a3412', border: '#ea580c', label: 'Refund' },
  ADVANCE_OVERRIDE:   { bg: '#ccfbf1', fg: '#115e59', border: '#14b8a6', label: 'Advance Override' },
  OVERDUE_ADVANCE:    { bg: '#fef3c7', fg: '#92400e', border: '#f59e0b', label: 'Overdue Advance' },
  // Awaiting pricing after the desk recorded what actually arrived. Blocks
  // check-out until the Booking Executive settles it.
  ARRIVAL_CHANGE:     { bg: '#e0e7ff', fg: '#3730a3', border: '#6366f1', label: 'Arrival Change' },
  // FO's request lane — discretionary ("decide whether to do this"), unlike
  // ARRIVAL_CHANGE which is an obligation ("price what already happened").
  // Approve = Resolved (+ response note to the desk); Reject = Declined.
  FO_CHANGE_REQUEST:  { bg: '#fae8ff', fg: '#86198f', border: '#d946ef', label: 'FO Request' },
  // Rooms marked no-show on a partially-arrived booking — "decide what the
  // absent pay" (policy-guided), vs ARRIVAL_CHANGE's "price what arrived".
  NO_SHOW_PRICING:    { bg: '#ffe4e6', fg: '#9f1239', border: '#f43f5e', label: 'No-Show Pricing' },
};

const HIGH_VALUE_THRESHOLD = 50000; // ₹50k — used by High-Value quick chip

// ============================================================================
// HELPERS
// ============================================================================

/** Returns the most-actionable identity for the approval's linked booking. */
const resolveBookedBy = (a: Approval): { primary: string; secondary?: string; kind: 'guest' | 'agent' | 'corporate' | 'ota' } => {
  switch ((a.bookingSource || '').toLowerCase()) {
    case 'travel agent':
      if (a.agentName) return { primary: a.agentName, secondary: a.agentCode || undefined, kind: 'agent' };
      break;
    case 'ota':
      if (a.otaDisplayName) return { primary: a.otaDisplayName, secondary: a.otaBookingRef || a.otaCode || undefined, kind: 'ota' };
      break;
    case 'corporate':
      if (a.corporateName) return { primary: a.corporateName, secondary: a.corporateCode || undefined, kind: 'corporate' };
      break;
  }
  return { primary: a.guestFullName?.trim() || '—', kind: 'guest' };
};

/** Relative-time label ("3h ago", "2d ago", "just now") for a past timestamp. */
const relativeAgo = (iso?: string | null): string => {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  const diffMs = Date.now() - t;
  if (diffMs < 60_000) return 'just now';
  const min = Math.round(diffMs / 60_000);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  const month = Math.round(day / 30);
  return `${month}mo ago`;
};

/** Pending-age label for an APPROVAL_PENDING row. Returns null for decided rows. */
const pendingAge = (iso?: string | null, status?: string | null): { label: string; color: string } | null => {
  if (status !== 'APPROVAL_PENDING' || !iso) return null;
  const diffHrs = (Date.now() - new Date(iso).getTime()) / 3_600_000;
  if (diffHrs < 1)  return { label: '< 1h', color: 'var(--text-muted)' };
  if (diffHrs < 24) return { label: `${Math.round(diffHrs)}h`, color: diffHrs > 12 ? '#92400e' : 'var(--text-secondary)' };
  const days = Math.round(diffHrs / 24);
  return { label: `${days}d`, color: days >= 2 ? '#991b1b' : '#92400e' };
};

/** Variance cell content adapted by approval type. */
const renderVariance = (a: Approval): React.ReactNode => {
  const muted = { color: 'var(--text-muted)' };
  switch (a.approvalType) {
    case 'DISCOUNT':
    case 'COMPLIMENTARY': {
      const amt = a.varianceAmount ?? 0;
      const pct = a.variancePercent;
      if (amt === 0 && pct == null) return <span style={muted}>—</span>;
      return (
        <span style={{ fontSize: '0.8rem' }}>
          −₹{formatCurrency(amt)}
          {pct != null && <span style={{ ...muted, marginLeft: '0.25rem' }}>({pct.toFixed(1)}%)</span>}
        </span>
      );
    }
    case 'ARRIVAL_CHANGE': {
      // No variance to show — what changed at arrival is on the booking, and the
      // executive prices it there. The value is the revised booking total.
      const total = a.totalValue ?? 0;
      return (
        <span style={{ fontSize: '0.8rem' }}>
          {total > 0 ? `₹${formatCurrency(total)}` : <span style={muted}>—</span>}
          <span style={{ ...muted, marginLeft: '0.25rem' }}>awaiting pricing</span>
        </span>
      );
    }
    case 'NO_SHOW_PRICING':
    case 'FO_CHANGE_REQUEST': {
      // The request TEXT is the substance — surface it where other types show
      // figures, truncated; the full text lives in the decision dialog.
      const text = a.justification?.trim() || '—';
      return (
        <span
          title={a.justification ?? undefined}
          style={{
            fontSize: '0.78rem', fontStyle: 'italic', color: 'var(--text-secondary)',
            display: 'inline-block', maxWidth: '260px', overflow: 'hidden',
            textOverflow: 'ellipsis', whiteSpace: 'nowrap', verticalAlign: 'bottom',
          }}
        >
          “{text}”
        </span>
      );
    }
    case 'RATE_BELOW_MINIMUM': {
      const req = a.requestedRate;
      const min = a.minimumRate;
      // Package context — the discount decision depends on size and period.
      const pkg = a.numNights && a.totalRooms
        ? ` · ${a.numNights}n × ${a.totalRooms}r`
        : '';
      if (req != null || min != null) {
        // Per-night shape: genuine rate-below-minimum.
        return (
          <span style={{ fontSize: '0.8rem' }}>
            req ₹{formatCurrency(req ?? 0)} <span style={muted}>/ min ₹{formatCurrency(min ?? 0)}{pkg}</span>
          </span>
        );
      }
      // Discount-driven shape: variance = TOTAL discount, totalValue = subtotal.
      const amt = a.varianceAmount ?? 0;
      const pct = a.variancePercent;
      if (amt === 0 && pct == null) return <span style={muted}>—</span>;
      return (
        <span style={{ fontSize: '0.8rem' }}>
          −₹{formatCurrency(amt)}
          {pct != null && <span style={{ ...muted, marginLeft: '0.25rem' }}>({pct.toFixed(1)}%)</span>}
          <span style={muted}>{pkg}</span>
        </span>
      );
    }
    case 'ADVANCE_OVERRIDE': {
      // varianceAmount carries amount-variance; metaData may carry days variance & proposed date.
      let proposedDate: string | null = null;
      if (a.metaData) {
        try {
          const parsed = JSON.parse(a.metaData);
          proposedDate = parsed?.proposed_advance_due_date || null;
        } catch { /* ignore */ }
      }
      const amt = a.varianceAmount ?? 0;
      const parts: React.ReactNode[] = [];
      if (amt !== 0) parts.push(<span key="amt">−₹{formatCurrency(Math.abs(amt))}</span>);
      if (proposedDate) parts.push(
        <span key="date" style={{ ...muted, marginLeft: parts.length ? '0.25rem' : 0 }}>
          → {proposedDate}
        </span>
      );
      return parts.length > 0 ? <span style={{ fontSize: '0.8rem' }}>{parts}</span> : <span style={muted}>—</span>;
    }
    case 'OVERDUE_ADVANCE': {
      const amt = a.varianceAmount ?? 0;
      return amt !== 0
        ? <span style={{ fontSize: '0.8rem' }}>Short ₹{formatCurrency(Math.abs(amt))}</span>
        : <span style={muted}>—</span>;
    }
    case 'REFUND': {
      const amt = a.varianceAmount ?? 0;
      return amt !== 0
        ? <span style={{ fontSize: '0.8rem' }}>₹{formatCurrency(Math.abs(amt))}</span>
        : <span style={muted}>—</span>;
    }
    default:
      return <span style={muted}>—</span>;
  }
};

/** Justification cell: prefixes ADVANCE_OVERRIDE rows with proposed date if present. */
const renderJustification = (a: Approval): { display: string; full: string } => {
  let prefix = '';
  if (a.approvalType === 'ADVANCE_OVERRIDE' && a.metaData) {
    try {
      const parsed = JSON.parse(a.metaData);
      const d = parsed?.proposed_advance_due_date;
      if (d) prefix = `Proposed ${d} — `;
    } catch { /* ignore */ }
  }
  const text = a.justification || '';
  return { display: prefix + (text || '—'), full: prefix + text };
};

// ============================================================================
// COMPONENT
// ============================================================================

interface ApprovalInboxScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  /**
   * Subset of {'add','edit','delete','view','export','approve'} the current
   * user may perform on this module — driven by admin.tbl_role_permissions
   * (passed through main_layout from MODULES_CONFIG.actions). Defaults to
   * [] (fail-closed): if the prop is missing, no action buttons render.
   */
  allowedActions?: string[];
  /**
   * Opens another module's tab. Supplied by main_layout; absent in tests and in
   * any host that renders this screen standalone, in which case the routing
   * deep link degrades to a hint rather than a dead button.
   */
  onNavigateModule?: (moduleId: string) => void;
}

const ApprovalInboxScreen: React.FC<ApprovalInboxScreenProps> = ({
  allowedActions = [],
  onNavigateModule,
}) => {
  // --- Server-side pagination & filter state ---
  // Default view = work queue: pending only, hideDecided=true (HideDecided toggle is on).
  const [queryParams, setQueryParams] = useState<ApprovalSearchRequest>({
    page: 1,
    pageSize: 25,
    hideDecided: true,
  });
  const { data, loading, pagination, refetch } = useApprovals(queryParams);
  // Refresh also reloads the other modules' band (the restaurant's requisitions).
  const [contributedKey, setContributedKey] = useState(0);

  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);

  // --- Filter inputs (uncommitted until Apply) ---
  const [filterStatuses, setFilterStatuses] = useState<string[]>([]);
  const [filterTypes, setFilterTypes] = useState<string[]>([]);
  const [filterProperty, setFilterProperty] = useState('');
  const [filterBookingNumber, setFilterBookingNumber] = useState('');
  const [filterRequestedFrom, setFilterRequestedFrom] = useState('');
  const [filterRequestedTo, setFilterRequestedTo] = useState('');
  const [filterDecidedFrom, setFilterDecidedFrom] = useState('');
  const [filterDecidedTo, setFilterDecidedTo] = useState('');
  const [filterOverdueOnly, setFilterOverdueOnly] = useState(false);
  const [filterHideDecided, setFilterHideDecided] = useState(true);

  // Active quick chip
  const [activeChip, setActiveChip] = useState<string | null>(null);

  // --- Role scoping (approvals follow AUTHORITY) ---
  // The server scopes the list to items routed to the caller's role(s) by
  // default. Seniors (crs_admin / crs_system) get an oversight toggle to see
  // every queue. WHICH role approves which type is configured in
  // Configuration -> Approval Rules (see the routing deep link below); the
  // senior test lives in config/rolePermissions so both surfaces agree.
  const isSenior = useMemo(() => isSeniorActor(), []);
  const [scope, setScope] = useState<'mine' | 'all'>('mine');

  /**
   * Deep link to the routing editor. It moved out of this screen: routing is
   * admin-only configuration, it belongs beside the thresholds that trigger the
   * approvals, and Configuration already owns the property-scope selector that
   * per-property routing needs. One editor, two doors - never a second editor.
   */
  const openRoutingConfig = useCallback(() => {
    requestConfigGroup('APPROVAL_RULES');
    if (onNavigateModule) {
      onNavigateModule(MODULE_IDS.CONFIG_SETTINGS);
    } else {
      notificationService.toast.info(
        'Approval routing is set in Setup -> Settings -> Approval Rules.');
    }
  }, [onNavigateModule]);

  const applyScope = useCallback((next: 'mine' | 'all') => {
    setScope(next);
    setQueryParams(prev => ({ ...prev, scope: next === 'all' ? 'all' : undefined, page: 1 }));
  }, []);

  // --- Modal state ---
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const [selectedApproval, setSelectedApproval] = useState<Approval | null>(null);
  /** Change item being priced in the Rate Worksheet. */
  const [worksheetApproval, setWorksheetApproval] = useState<Approval | null>(null);
  /** Linked booking opened read-only from a row. */
  const [viewBooking, setViewBooking] = useState<Booking | null>(null);

  // Quick approve / reject confirm dialogs
  const [quickDecisionOpen, setQuickDecisionOpen] = useState<{ kind: 'approve' | 'reject'; row: Approval } | null>(null);
  const [quickDecisionNotes, setQuickDecisionNotes] = useState('');
  const [quickDecisionSaving, setQuickDecisionSaving] = useState(false);

  // --- Server-side debounced search → searchTerm in queryParams ---
  useEffect(() => {
    const handle = setTimeout(() => {
      setQueryParams(prev => ({ ...prev, searchTerm: searchQuery || undefined, page: 1 }));
    }, 400);
    return () => clearTimeout(handle);
  }, [searchQuery]);

  // --- Pagination handlers ---
  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams(prev => ({ ...prev, page: pageIndex + 1 }));
  }, []);
  const handlePageSizeChange = useCallback((pageSize: number) => {
    setQueryParams(prev => ({ ...prev, pageSize, page: 1 }));
  }, []);

  // --- Async dropdown loaders ---
  const loadPropertyOptions = useCallback(async (inputValue: string): Promise<SelectOption[]> => {
    const res = await propertyService.getAll({ searchTerm: inputValue || undefined, pageSize: inputValue ? 50 : 500, includeInactive: false });
    return res.data.map((p: any) => ({ value: p.id, label: `${p.propertyCode} - ${p.propertyName}` }));
  }, []);

  // --- Filter Apply / Clear ---
  const handleApplyFilters = useCallback(() => {
    setActiveChip(null);
    setQueryParams(prev => ({
      ...prev,
      approvalStatusCodes: filterStatuses.length > 0 ? filterStatuses : undefined,
      approvalStatusCode: undefined,
      approvalTypes: filterTypes.length > 0 ? filterTypes : undefined,
      approvalType: undefined,
      propertyId: filterProperty || undefined,
      // bookingId here doesn't apply because the user types a booking *number*,
      // not an id — we pipe that through searchTerm so the existing ILIKE finds it.
      searchTerm: (filterBookingNumber || prev.searchTerm) || undefined,
      requestedFrom: filterRequestedFrom ? new Date(filterRequestedFrom + 'T00:00:00').toISOString() : undefined,
      requestedTo: filterRequestedTo ? new Date(filterRequestedTo + 'T23:59:59.999').toISOString() : undefined,
      decidedFrom: filterDecidedFrom ? new Date(filterDecidedFrom + 'T00:00:00').toISOString() : undefined,
      decidedTo: filterDecidedTo ? new Date(filterDecidedTo + 'T23:59:59.999').toISOString() : undefined,
      overdueOnly: filterOverdueOnly || undefined,
      hideDecided: filterHideDecided,
      page: 1,
    }));
    setFilterOpen(false);
  }, [filterStatuses, filterTypes, filterProperty, filterBookingNumber, filterRequestedFrom, filterRequestedTo, filterDecidedFrom, filterDecidedTo, filterOverdueOnly, filterHideDecided]);

  const handleClearFilters = useCallback(() => {
    setActiveChip(null);
    setFilterStatuses([]);
    setFilterTypes([]);
    setFilterProperty('');
    setFilterBookingNumber('');
    setFilterRequestedFrom('');
    setFilterRequestedTo('');
    setFilterDecidedFrom('');
    setFilterDecidedTo('');
    setFilterOverdueOnly(false);
    setFilterHideDecided(true);
    setQueryParams({ page: 1, pageSize: queryParams.pageSize ?? 25, hideDecided: true });
  }, [queryParams.pageSize]);

  // --- Quick-filter chips ---
  const applyChip = useCallback((chipId: string) => {
    if (activeChip === chipId) {
      setActiveChip(null);
      handleClearFilters();
      return;
    }
    setActiveChip(chipId);
    const today = new Date().toISOString().slice(0, 10);
    let next: Partial<ApprovalSearchRequest> = {
      page: 1,
      approvalStatusCode: undefined,
      approvalStatusCodes: undefined,
      approvalType: undefined,
      approvalTypes: undefined,
      overdueOnly: undefined,
      hideDecided: true,
      decidedFrom: undefined,
      decidedTo: undefined,
    };
    switch (chipId) {
      case 'my-queue':
        next.approvalStatusCodes = ['APPROVAL_PENDING'];
        break;
      case 'overdue':
        next.approvalStatusCodes = ['APPROVAL_PENDING'];
        next.overdueOnly = true;
        break;
      case 'decided-today':
        next.approvalStatusCodes = ['APPROVED', 'REJECTED'];
        next.hideDecided = false;
        next.decidedFrom = new Date(today + 'T00:00:00').toISOString();
        next.decidedTo = new Date(today + 'T23:59:59.999').toISOString();
        break;
      case 'advance-related':
        next.approvalTypes = ['ADVANCE_OVERRIDE', 'OVERDUE_ADVANCE'];
        break;
      case 'booking-changes':
        // The FO ↔ booking-staff work queue: unpriced arrival changes (an
        // obligation) + FO requests (discretionary). Open items persist past
        // departure — a departed booking's unpriced change is MORE urgent,
        // never hidden (design §4).
        next.approvalTypes = ['ARRIVAL_CHANGE', 'FO_CHANGE_REQUEST', 'NO_SHOW_PRICING'];
        next.approvalStatusCodes = ['APPROVAL_PENDING'];
        break;
      case 'high-value':
        // Backend doesn't filter by totalValue server-side yet; client rationalises by
        // sorting + the user can scan the list. Same chip semantics as the BookingList
        // chips: just sets a status filter so the visible queue is manageable.
        next.approvalStatusCodes = ['APPROVAL_PENDING'];
        break;
    }
    setQueryParams(prev => ({ ...prev, ...next }));
  }, [activeChip, handleClearFilters]);

  // --- Row actions ---
  const handleView = useCallback((approval: Approval) => {
    setSelectedApproval(approval);
    setActionModalOpen(true);
  }, []);

  const handleViewBooking = useCallback(async (approval: Approval) => {
    if (!approval.bookingId) {
      notificationService.toast.warning('No booking linked to this approval.');
      return;
    }
    try {
      const res = await bookingService.getById(approval.bookingId);
      if (res.data) setViewBooking(res.data);
      else notificationService.toast.error('Failed to load the booking.');
    } catch {
      notificationService.toast.error('Failed to load the booking.');
    }
  }, []);

  const openQuickDecision = useCallback((kind: 'approve' | 'reject', row: Approval) => {
    setQuickDecisionNotes('');
    setQuickDecisionOpen({ kind, row });
  }, []);

  const executeQuickDecision = useCallback(async () => {
    if (!quickDecisionOpen) return;
    const { kind, row } = quickDecisionOpen;
    // Post-arrival items (FO request, arrival change, no-show pricing) are never
    // "approved": they are re-priced in the Rate Worksheet, closed WITHOUT a price
    // change, or declined — and the last two always say why (the desk tells the guest).
    const postArrival = isPostArrivalItem(row.approvalType);
    if (postArrival && quickDecisionNotes.trim().length < 3) {
      notificationService.toast.warning(kind === 'approve'
        ? 'Say why the request is closed without a price change — the desk sees it.'
        : 'Give the front office a reason for declining the request.');
      return;
    }
    setQuickDecisionSaving(true);
    try {
      const body = {
        notes: quickDecisionNotes.trim() || undefined,
        rowVersion: row.rowVersion,
        resolution: postArrival && kind === 'approve' ? 'NO_CHANGE' as const : undefined,
      };
      if (kind === 'approve') {
        await approvalService.approve(row.approvalRequestId, body);
        notificationService.toast.success(postArrival ? 'Closed without a price change — the desk sees your note.' : 'Approval granted.');
      } else {
        await approvalService.reject(row.approvalRequestId, body);
        notificationService.toast.success(postArrival ? 'Request declined — the desk sees your reason.' : 'Approval rejected.');
      }
      setQuickDecisionOpen(null);
      refetch();
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || `Failed to ${kind}.`;
      notificationService.toast.error(msg);
    } finally {
      setQuickDecisionSaving(false);
    }
  }, [quickDecisionOpen, quickDecisionNotes, refetch]);

  const getRowActions = useCallback((row: Approval): ActionMenuItem[] => {
    const isPending = row.approvalStatusCode === 'APPROVAL_PENDING';
    const actions: ActionMenuItem[] = [];
    if (allowedActions.includes('view')) {
      actions.push({ id: 'view', label: isPending ? 'View / Decide' : 'View', icon: <IconSystem.eye size={16} />, onClick: () => handleView(row) });
    }
    // Rate Worksheet — the pricing lane for change items (works on in-house /
    // departed bookings the full edit refuses). Gate on 'approve': the acting
    // user is the pricing authority the item is routed to.
    const isChangeItem = row.approvalType === 'ARRIVAL_CHANGE' || row.approvalType === 'FO_CHANGE_REQUEST'
      || row.approvalType === 'NO_SHOW_PRICING';
    if (isPending && isChangeItem && row.bookingId && allowedActions.includes('approve')) {
      actions.push({
        id: 'worksheet', label: 'Re-price (Rate Worksheet)', icon: <IconSystem.edit size={16} />,
        onClick: () => setWorksheetApproval(row),
      });
    }
    // Quick approve / reject — gate on 'approve' permission. Post-arrival items
    // have no plain approve: re-pricing is the worksheet above; this closes the
    // item WITHOUT a price change (reason required) or declines it.
    if (isPending && allowedActions.includes('approve')) {
      const postArrival = isPostArrivalItem(row.approvalType);
      actions.push(
        {
          id: 'quick-approve', label: postArrival ? 'Close — no price change…' : 'Quick Approve',
          icon: <IconSystem.check size={16} />, onClick: () => openQuickDecision('approve', row),
        },
        {
          id: 'quick-reject', label: postArrival ? 'Decline…' : 'Quick Reject',
          icon: <IconSystem.x size={16} />, onClick: () => openQuickDecision('reject', row), variant: 'danger',
        },
      );
    }
    // "View Booking" is a navigation affordance to a related record — gate on 'view'
    if (row.bookingId && allowedActions.includes('view')) {
      actions.push({ id: 'view-booking', label: 'View Booking', icon: <IconSystem.link size={16} />, onClick: () => handleViewBooking(row) });
    }
    return actions;
  }, [allowedActions, handleView, handleViewBooking, openQuickDecision]);

  // --- Columns ---
  const columns: SyutGridColumn<Approval>[] = useMemo(() => [
    {
      id: 'approvalType', header: 'Type', accessorKey: 'approvalType', size: 145, enableSorting: false,
      cell: ({ row }) => {
        const t = TYPE_STYLE[row.original.approvalType] || { bg: '#e5e7eb', fg: '#374151', border: '#9ca3af', label: row.original.approvalType };
        return (
          <span style={{
            display: 'inline-flex', alignItems: 'center',
            fontSize: '0.7rem', fontWeight: 600,
            padding: '0.125rem 0.5rem', borderRadius: '999px',
            background: t.bg, color: t.fg,
            borderLeft: `3px solid ${t.border}`,
          }}>{t.label}</span>
        );
      },
    },
    {
      id: 'bookingNumber', header: 'Booking #', accessorKey: 'bookingNumber', size: 150, enableSorting: false,
      cell: ({ row }) => {
        const b = row.original;
        // Urgency dot for pending rows: red >48h, amber >24h.
        let dot: { color: string; title: string } | null = null;
        if (b.approvalStatusCode === 'APPROVAL_PENDING' && b.requestedAt) {
          const hrs = (Date.now() - new Date(b.requestedAt).getTime()) / 3_600_000;
          if (hrs >= 48)      dot = { color: '#dc2626', title: 'Pending > 48h' };
          else if (hrs >= 24) dot = { color: '#f59e0b', title: 'Pending > 24h' };
        }
        // A change item whose booking already departed: the guard defaulted OFF
        // means this is the compensating control — flag it loudly, never hide it.
        const departed = b.approvalStatusCode === 'APPROVAL_PENDING'
          && b.bookingStatusCode === 'CHECKED_OUT'
          && (b.approvalType === 'ARRIVAL_CHANGE' || b.approvalType === 'FO_CHANGE_REQUEST'
            || b.approvalType === 'NO_SHOW_PRICING');
        return (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.85rem', fontWeight: 600, color: 'var(--primary)' }}>
            {dot && <span title={dot.title} style={{ width: 8, height: 8, borderRadius: '50%', background: dot.color, flexShrink: 0 }} />}
            {b.bookingNumber || '—'}
            {departed && (
              <span
                title="The guest has departed with this item still open — settle it before it silently becomes lost revenue."
                style={{
                  fontSize: '0.6rem', fontWeight: 700, padding: '0.05rem 0.4rem',
                  borderRadius: '999px', background: 'rgba(185,28,28,.1)',
                  color: '#b91c1c', border: '1px solid rgba(185,28,28,.3)',
                  letterSpacing: '0.03em', flexShrink: 0,
                }}
              >
                DEPARTED
              </span>
            )}
          </span>
        );
      },
    },
    {
      id: 'propertyName', header: 'Property', accessorKey: 'propertyName', size: 170, enableSorting: false,
      cell: ({ row }) => (
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
          <span style={{ fontSize: '0.8rem' }}>{row.original.propertyName || '—'}</span>
          {row.original.propertyCode && (
            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{row.original.propertyCode}</span>
          )}
        </div>
      ),
    },
    {
      id: 'bookedBy', header: 'Booked By', accessorKey: 'guestFullName', size: 200, enableSorting: false,
      cell: ({ row }) => {
        const r = resolveBookedBy(row.original);
        const kindIcon = {
          guest:     <IconSystem.user size={12} />,
          agent:     <IconSystem.tag size={12} />,
          corporate: <IconSystem.home size={12} />,
          ota:       <IconSystem.link size={12} />,
        }[r.kind];
        return (
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
            <span style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
              <span style={{ color: 'var(--text-muted)' }}>{kindIcon}</span>
              <span style={{ fontWeight: 500 }}>{r.primary}</span>
            </span>
            {r.secondary && (
              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{r.secondary}</span>
            )}
          </div>
        );
      },
    },
    {
      id: 'requestedByName', header: 'Requested By', accessorKey: 'requestedByName', size: 130, enableSorting: false,
      cell: ({ row }) => <span style={{ fontSize: '0.8rem' }}>{row.original.requestedByName || '—'}</span>,
    },
    {
      id: 'requestedAt', header: 'Requested', accessorKey: 'requestedAt', size: 130, enableSorting: false,
      cell: ({ row }) => {
        const iso = row.original.requestedAt;
        const exact = iso ? new Date(iso).toLocaleString() : '';
        return (
          <span title={exact} style={{ fontSize: '0.8rem' }}>{relativeAgo(iso)}</span>
        );
      },
    },
    {
      id: 'varianceAmount', header: 'Variance', accessorKey: 'varianceAmount', size: 165, enableSorting: false,
      cell: ({ row }) => renderVariance(row.original),
    },
    {
      id: 'totalValue', header: 'Total Value', accessorKey: 'totalValue', size: 110, enableSorting: false,
      cell: ({ row }) => {
        const v = row.original.totalValue;
        return v != null
          ? <span style={{ fontSize: '0.8rem', textAlign: 'right', display: 'block' }}>₹{formatCurrency(v)}</span>
          : <span style={{ color: 'var(--text-muted)' }}>—</span>;
      },
    },
    {
      id: 'justification', header: 'Justification', accessorKey: 'justification', size: 280, enableSorting: false,
      cell: ({ row }) => {
        const j = renderJustification(row.original);
        return (
          <span
            title={j.full || ''}
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontSize: '0.78rem',
              lineHeight: 1.3,
              wordBreak: 'break-word',
            }}
          >
            {j.display}
          </span>
        );
      },
    },
    {
      id: 'pendingAge', header: 'Pending Age', accessorKey: 'requestedAt', size: 105, enableSorting: false,
      cell: ({ row }) => {
        const age = pendingAge(row.original.requestedAt, row.original.approvalStatusCode);
        if (!age) return <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>—</span>;
        return (
          <span style={{ fontSize: '0.8rem', fontWeight: 600, color: age.color }}>{age.label}</span>
        );
      },
    },
    {
      id: 'approvalStatusCode', header: 'Status', accessorKey: 'approvalStatusCode', size: 110, enableSorting: false,
      cell: ({ row }) => {
        const code = row.original.approvalStatusCode || '';
        const badge = STATUS_BADGE[code] || { bg: '#e5e7eb', fg: '#374151', label: code };
        return (
          <span style={{
            fontSize: '0.7rem', fontWeight: 600, padding: '0.125rem 0.5rem',
            borderRadius: '0.25rem', background: badge.bg, color: badge.fg,
          }}>{badge.label}</span>
        );
      },
    },
    {
      id: 'decided', header: 'Decided', accessorKey: 'approvedAt', size: 170, enableSorting: false,
      cell: ({ row }) => {
        const a = row.original;
        if (!a.approvedAt) return <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>—</span>;
        const exact = new Date(a.approvedAt).toLocaleString();
        // Self-decision: allowed by design (small properties, one person, both
        // hats) but never silent — audited server-side, flagged here.
        const self = !!a.approvedBy && a.approvedBy === a.requestedBy;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
            <span style={{ fontSize: '0.8rem' }}>
              {a.approvedByName || '—'}
              {self && (
                <span
                  title="Decided by the same user who raised it — permitted, audited, and flagged for oversight."
                  style={{
                    marginLeft: '0.35rem', fontSize: '0.62rem', fontWeight: 700,
                    padding: '0.05rem 0.35rem', borderRadius: '999px',
                    background: 'rgba(245,158,11,.15)', color: '#b45309',
                    border: '1px solid rgba(245,158,11,.4)', verticalAlign: 'middle',
                  }}
                >
                  SELF
                </span>
              )}
            </span>
            <span title={exact} style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{relativeAgo(a.approvedAt)}</span>
          </div>
        );
      },
    },
  ], []);

  // --- Action buttons ---
  const actionButtons: ActionButton[] = useMemo(() => {
    const buttons: ActionButton[] = [];
    if (isSenior) {
      buttons.push({
        id: 'routing', icon: <IconSystem.settings size={16} />, label: 'Routing',
        onClick: openRoutingConfig, variant: 'secondary',
      });
    }
    buttons.push({
      id: 'refresh', icon: <IconSystem.refresh size={16} />, label: 'Refresh',
      onClick: () => { refetch(); setContributedKey((k) => k + 1); }, variant: 'icon',
    });
    return buttons;
  }, [refetch, isSenior, openRoutingConfig]);

  // High-Value chip needs a client-side sort lift; we sort displayed data by totalValue desc when active.
  const displayedData = useMemo(() => {
    if (activeChip === 'high-value') {
      return [...data].sort((a, b) => (b.totalValue ?? 0) - (a.totalValue ?? 0))
        .filter(r => (r.totalValue ?? 0) >= HIGH_VALUE_THRESHOLD);
    }
    return data;
  }, [data, activeChip]);

  // --- Applied-filter count for the filter button badge ---
  // Counts the structured filters in queryParams (excluding pagination, search,
  // and the inbox default `hideDecided=true`).
  const appliedFilterCount = useMemo(() => {
    let n = 0;
    if (queryParams.approvalStatusCodes && queryParams.approvalStatusCodes.length > 0) n++;
    else if (queryParams.approvalStatusCode)                    n++;
    if (queryParams.approvalTypes && queryParams.approvalTypes.length > 0) n++;
    else if (queryParams.approvalType)                          n++;
    if (queryParams.propertyId)                                 n++;
    if (queryParams.requestedById)                              n++;
    if (queryParams.requestedFrom || queryParams.requestedTo)   n++;
    if (queryParams.decidedFrom || queryParams.decidedTo)       n++;
    if (queryParams.overdueOnly)                                n++;
    if (queryParams.hideDecided === false)                      n++; // explicitly off counts as a filter departure from default
    return n;
  }, [queryParams]);

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Approval Inbox"
        description="Review and act on pending approval requests."
        icon={<IconSystem.checkCircle size={18} />}
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search booking #, justification, requester, property, OTA, agent...',
        }}
        filterConfig={{ onClick: () => setFilterOpen(!filterOpen), isActive: filterOpen, appliedCount: appliedFilterCount }}
        actions={actionButtons}
      />

      {/* Quick-filter chips row */}
      <div style={{
        padding: '6px 1.25rem 0',
        display: 'flex', flexWrap: 'wrap', gap: '0.375rem', alignItems: 'center',
      }}>
        {isSenior && (
          <span style={{ display: 'inline-flex', borderRadius: '999px', border: '1px solid var(--border-color)', overflow: 'hidden', marginRight: '0.5rem' }}>
            {([['mine', 'My approvals'], ['all', 'All items']] as const).map(([id, label]) => (
              <button
                key={id}
                onClick={() => applyScope(id)}
                title={id === 'all' ? 'Oversight view — every role’s queue (senior only)' : 'Items routed to your role'}
                style={{
                  padding: '0.25rem 0.625rem', fontSize: '0.7rem', fontWeight: 600,
                  cursor: 'pointer', border: 'none',
                  background: scope === id ? 'var(--primary)' : 'var(--bg-primary)',
                  color: scope === id ? 'white' : 'var(--text-primary)',
                }}
              >
                {label}
              </button>
            ))}
          </span>
        )}
        <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: 600, marginRight: '0.25rem' }}>
          QUICK VIEWS
        </span>
        {[
          { id: 'my-queue',        label: 'My Queue',         icon: <IconSystem.clock size={12} /> },
          { id: 'overdue',         label: 'Overdue (>24h)',   icon: <IconSystem.warning size={12} /> },
          { id: 'decided-today',   label: 'Decided Today',    icon: <IconSystem.checkCircle size={12} /> },
          { id: 'advance-related', label: 'Advance-Related',  icon: <IconSystem.dollarSign size={12} /> },
          { id: 'booking-changes', label: 'Booking Changes',  icon: <IconSystem.edit size={12} /> },
          { id: 'high-value',      label: `High-Value (≥ ₹${(HIGH_VALUE_THRESHOLD/1000).toFixed(0)}k)`, icon: <IconSystem.star size={12} /> },
        ].map(chip => {
          const active = activeChip === chip.id;
          return (
            <button
              key={chip.id}
              onClick={() => applyChip(chip.id)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
                padding: '0.25rem 0.625rem', borderRadius: '999px',
                fontSize: '0.7rem', fontWeight: 500, cursor: 'pointer',
                border: active ? '1px solid var(--primary)' : '1px solid var(--border-color)',
                background: active ? 'var(--primary)' : 'var(--bg-primary)',
                color: active ? 'white' : 'var(--text-primary)',
                transition: 'all 0.15s ease',
              }}
            >
              {chip.icon}
              {chip.label}
            </button>
          );
        })}
        {activeChip && (
          <button
            onClick={() => { setActiveChip(null); handleClearFilters(); }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.25rem',
              padding: '0.25rem 0.5rem', borderRadius: '999px',
              fontSize: '0.7rem', fontWeight: 500, cursor: 'pointer',
              border: 'none', background: 'transparent', color: 'var(--text-muted)',
            }}
            title="Clear quick view"
          >
            <IconSystem.x size={12} /> Clear
          </button>
        )}
      </div>

      <ContributedApprovalsPanel refreshKey={contributedKey} />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div className="data-grid-container">
          <SyutGrid
            data={displayedData}
            columns={columns}
            enableRowSelection={false}
            enablePagination
            manualPagination
            totalCount={pagination.totalItems}
            currentPage={(queryParams.page ?? 1) - 1}
            pageSize={queryParams.pageSize ?? 25}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            enableSorting={false}
            rowActions={getRowActions}
            frozenLeftColumns={['approvalType', 'bookingNumber']}
            frozenRightColumns={['actions']}
            isLoading={loading}
            emptyMessage="No approval requests match the current filters"
          />
        </div>

        {filterOpen && <div className="search-panel">
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button
              className="search-panel-close"
              onClick={() => setFilterOpen(false)}
              title="Close filters"
            >
              <IconSystem.x size={14} />
            </button>
          </div>
          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect
                label="Status"
                isMulti
                options={STATUS_OPTIONS}
                value={filterStatuses}
                onChange={(val) => setFilterStatuses(Array.isArray(val) ? val : [])}
                placeholder="All Statuses"
                isClearable
                size="sm"
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Type"
                isMulti
                options={TYPE_OPTIONS}
                value={filterTypes}
                onChange={(val) => setFilterTypes(Array.isArray(val) ? val : [])}
                placeholder="All Types"
                isClearable
                size="sm"
              />
            </div>
            <div className="filter-section">
              <SyutSelect
                label="Property"
                isAsync
                loadOptions={loadPropertyOptions}
                defaultOptions={true}
                cacheOptions={false}
                value={filterProperty}
                onChange={(val) => setFilterProperty((val as string) || '')}
                placeholder="All Properties"
                isClearable
                size="sm"
              />
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Booking #</label>
              <input
                type="text"
                className="form-input"
                value={filterBookingNumber}
                onChange={(e) => setFilterBookingNumber(e.target.value)}
                placeholder="Booking number"
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Requested From</label>
              <input
                type="date"
                className="form-input"
                value={filterRequestedFrom}
                onChange={(e) => setFilterRequestedFrom(e.target.value)}
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Requested To</label>
              <input
                type="date"
                className="form-input"
                value={filterRequestedTo}
                onChange={(e) => setFilterRequestedTo(e.target.value)}
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Decided From</label>
              <input
                type="date"
                className="form-input"
                value={filterDecidedFrom}
                onChange={(e) => setFilterDecidedFrom(e.target.value)}
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>
            <div className="filter-section">
              <label className="form-label" style={{ fontSize: '0.8125rem', marginBottom: '0.25rem' }}>Decided To</label>
              <input
                type="date"
                className="form-input"
                value={filterDecidedTo}
                onChange={(e) => setFilterDecidedTo(e.target.value)}
                style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
              />
            </div>
            <div className="filter-section">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8rem' }}>
                <input type="checkbox" checked={filterOverdueOnly} onChange={(e) => setFilterOverdueOnly(e.target.checked)} />
                Overdue Only (&gt; 24h pending)
              </label>
            </div>
            <div className="filter-section">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', fontSize: '0.8rem' }}>
                <input type="checkbox" checked={filterHideDecided} onChange={(e) => setFilterHideDecided(e.target.checked)} />
                Hide Decided (work-queue mode)
              </label>
            </div>
          </div>
          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={handleClearFilters}>Clear</button>
            <button className="btn btn-primary" onClick={handleApplyFilters}>Apply</button>
          </div>
        </div>}
      </div>

      <ApprovalActionModal
        isOpen={actionModalOpen}
        approval={selectedApproval}
        onClose={() => { setActionModalOpen(false); setSelectedApproval(null); }}
        onActed={() => { refetch(); }}
        onReprice={allowedActions.includes('approve') ? (a) => setWorksheetApproval(a) : undefined}
      />

      {/* Rate Worksheet — price the change item, optionally resolve it in one motion. */}
      <RateWorksheetModal
        isOpen={!!worksheetApproval}
        approval={worksheetApproval}
        canApprove={allowedActions.includes('approve')}
        onClose={() => setWorksheetApproval(null)}
        onApplied={() => { refetch(); }}
      />

      {/* Read-only booking context from a row's View Booking action. */}
      <BookingViewModal
        isOpen={!!viewBooking}
        booking={viewBooking}
        onClose={() => setViewBooking(null)}
      />

      {/* Quick approve / reject inline modal (with notes textarea) */}
      {quickDecisionOpen && (() => {
        const row = quickDecisionOpen.row;
        const approving = quickDecisionOpen.kind === 'approve';
        const postArrival = isPostArrivalItem(row.approvalType);
        const money = (v?: number | null) => (v == null ? '—' : `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`);
        const title = postArrival
          ? (approving ? 'Close — no price change' : 'Decline request')
          : (approving ? 'Quick Approve' : 'Quick Reject');
        return (
        <>
          <div className="modal-backdrop" onClick={() => !quickDecisionSaving && setQuickDecisionOpen(null)} />
          <div className="modal-container modal-sm">
            <div className="modal-header" style={{ minHeight: 'auto' }}>
              <h2 className="modal-title">{title}</h2>
              <button className="modal-close" onClick={() => setQuickDecisionOpen(null)} disabled={quickDecisionSaving}>
                <IconSystem.x size={20} />
              </button>
            </div>
            <div className="modal-content">
              <p style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: 0 }}>
                {postArrival
                  ? (approving
                      ? <>Close the {TYPE_STYLE[row.approvalType]?.label || row.approvalType} on booking <strong>{row.bookingNumber || '—'}</strong> <b>without changing its price</b>? To change the price, use <b>Re-price (Rate Worksheet)</b> instead.</>
                      : <>Decline the {TYPE_STYLE[row.approvalType]?.label || row.approvalType} on booking <strong>{row.bookingNumber || '—'}</strong>?</>)
                  : <>{approving ? 'Approve' : 'Reject'} approval for booking <strong>{row.bookingNumber || '—'}</strong> ({TYPE_STYLE[row.approvalType]?.label || row.approvalType})?</>}
              </p>
              {/* The request's full text — the decision is about this sentence. */}
              {postArrival && row.justification && (
                <div style={{
                  border: '1px solid rgba(217,70,239,.35)', background: 'rgba(217,70,239,.06)',
                  borderRadius: '7px', padding: '0.5rem 0.7rem', marginBottom: '0.75rem',
                  fontSize: '0.82rem', color: 'var(--text-primary)', fontStyle: 'italic',
                }}>
                  “{row.justification}”
                  {row.requestedAmount != null && (
                    <div style={{ fontStyle: 'normal', marginTop: '0.3rem', fontSize: '0.78rem' }}>
                      Asked for <b>{money(row.requestedAmount)}</b> {requestedBasisLabel(row.requestedBasis)} · booking total now <b>{money(row.bookingCurrentTotal)}</b>
                    </div>
                  )}
                </div>
              )}
              <div className="form-group">
                <label className="form-label">
                  {postArrival
                    ? <>{approving ? 'Why no price change' : 'Reason for declining'} — shown to the front office <span className="text-danger">*</span></>
                    : 'Notes (optional)'}
                </label>
                <textarea
                  className="form-input form-textarea"
                  rows={3}
                  placeholder={postArrival
                    ? 'Shown to the desk on the check-in screen…'
                    : 'Optional notes for the requester...'}
                  value={quickDecisionNotes}
                  onChange={(e) => setQuickDecisionNotes(e.target.value)}
                  disabled={quickDecisionSaving}
                  style={{ fontSize: '0.85rem' }}
                />
              </div>
            </div>
            <div className="modal-footer" style={{ minHeight: 'auto' }}>
              <button
                className="btn btn-secondary"
                onClick={() => setQuickDecisionOpen(null)}
                disabled={quickDecisionSaving}
              >Cancel</button>
              <button
                className={`btn ${approving ? 'btn-primary' : 'btn-danger'}`}
                onClick={executeQuickDecision}
                disabled={quickDecisionSaving || (postArrival && quickDecisionNotes.trim().length < 3)}
              >
                {quickDecisionSaving
                  ? (approving ? 'Saving...' : 'Declining...')
                  : postArrival
                    ? (approving ? 'Close — no price change' : 'Decline')
                    : (approving ? 'Approve' : 'Reject')}
              </button>
            </div>
          </div>
        </>
        );
      })()}
    </div>
  );
};

export default ApprovalInboxScreen;
